import { useState } from 'react'
import type { BlissBasicConfiguratorChoice } from '../../config/blissBasicCatalog'
import { formatChoicePrice } from '../../utils/blissBasicConfiguratorDefaults'
import BlissBasicMattressLayoutViz from './BlissBasicMattressLayoutViz'

type Props = {
  items: BlissBasicConfiguratorChoice[]
  multiSelect: boolean
  selectedKeys: string[]
  onChange: (keys: string[]) => void
  ariaLabel: string
  /** Customer funnel — never show price ranges on option cards. */
  showPrices?: boolean
  /** Layout step — show mattress footprint diagrams. */
  mattressLayoutMode?: boolean
}

export default function BlissBasicExpandableOptions({
  items,
  multiSelect,
  selectedKeys,
  onChange,
  ariaLabel,
  showPrices = false,
  mattressLayoutMode = false,
}: Props) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  const toggleExpand = (key: string) => {
    setExpandedKey((prev) => (prev === key ? null : key))
  }

  const selectOne = (key: string) => {
    onChange([key])
  }

  const toggleMulti = (key: string) => {
    if (selectedKeys.includes(key)) {
      onChange(selectedKeys.filter((k) => k !== key))
    } else {
      onChange([...selectedKeys, key])
    }
  }

  return (
    <div className="bb-config-options" role={multiSelect ? 'group' : 'radiogroup'} aria-label={ariaLabel}>
      {items.map((item) => {
        const selected = selectedKeys.includes(item.choiceKey)
        const expanded = expandedKey === item.choiceKey
        const priceText = showPrices ? formatChoicePrice(item) : null
        return (
          <div
            key={item.choiceKey}
            className={`bb-config-option-wrap${selected ? ' bb-config-option-wrap--selected' : ''}${expanded ? ' bb-config-option-wrap--open' : ''}`}
          >
            <div className="bb-config-option-head">
              <button
                type="button"
                className="bb-config-option-select"
                aria-pressed={selected}
                onClick={() => (multiSelect ? toggleMulti(item.choiceKey) : selectOne(item.choiceKey))}
              >
                <input
                  type={multiSelect ? 'checkbox' : 'radio'}
                  readOnly
                  checked={selected}
                  tabIndex={-1}
                  aria-hidden
                />
                <span className="bb-config-option-label">{item.label}</span>
              </button>
              <button
                type="button"
                className="bb-config-option-expand"
                aria-expanded={expanded}
                aria-label={expanded ? 'Hide details' : 'Show details'}
                onClick={() => toggleExpand(item.choiceKey)}
              >
                {expanded ? '−' : '+'}
              </button>
            </div>
            {mattressLayoutMode && !expanded ? (
              <BlissBasicMattressLayoutViz choiceKey={item.choiceKey} compact />
            ) : null}
            {item.detail && !expanded ? (
              <p className="bb-config-option-teaser">{item.detail}</p>
            ) : null}
            {expanded && (
              <div className="bb-config-option-details">
                {mattressLayoutMode ? (
                  <BlissBasicMattressLayoutViz choiceKey={item.choiceKey} />
                ) : null}
                {item.photoUrl ? (
                  <img src={item.photoUrl} alt="" className="bb-config-option-photo" loading="lazy" />
                ) : null}
                {item.detail ? <p className="bb-config-option-hint">{item.detail}</p> : null}
                {priceText ? <p className="bb-config-option-price">{priceText}</p> : null}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
